import { timeZones } from './timeZones.js';

/**
 * Org-level scheduling: an organization's `timeZone` and its
 * `weeklyReportGenerationTime` (`{ weekday, time }`).
 *
 * The weekly deal summary cron selects orgs by the two together, so every
 * member of an org gets the summary on the org's own day and time, in the
 * org's own timezone -- a member's personal timezone no longer decides when it
 * runs. Org admins set both from the seller app; this console can set them for
 * any org through `PUT /ads/org/{orgId}`.
 *
 * The API validates the pair strictly (real weekday, `HH:mm` on a 5-minute
 * boundary, both present together), so the pickers only offer values that pass.
 *
 * Mirrors sb-main `src/utils/orgSchedule.js`; keep the two in sync.
 */

/** Weekdays, in the order the pickers list them. Wire values are uppercase. */
export const WEEKDAYS = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

/**
 * The cron consuming this field runs every 5 minutes and matches an exact
 * `weekday` + `time` pair, so a time off the boundary could never fire and the
 * API rejects it with a 400.
 */
export const REPORT_TIME_STEP_MINUTES = 5;

/** What the backend falls back to for an org that has never set one. */
export const DEFAULT_WEEKLY_REPORT_SCHEDULE = {
  weekday: 'SUNDAY',
  time: '00:00',
};

/** Timezone the backend falls back to when nothing better can be derived. */
export const DEFAULT_TIME_ZONE = 'America/Los_Angeles';

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `"09:05"` -> true; `"09:07"`, `"9:05"` and `"24:00"` -> false. */
export const isValidReportTime = (time) => {
  const match = TIME_PATTERN.exec(time || '');
  if (!match) return false;
  return Number(match[2]) % REPORT_TIME_STEP_MINUTES === 0;
};

/** `"sunday"` / `"SUNDAY"` -> `"SUNDAY"`; anything else -> `null`. */
export const normalizeWeekday = (weekday) => {
  const upper = String(weekday || '').toUpperCase();
  return WEEKDAYS.some((day) => day.value === upper) ? upper : null;
};

/**
 * Every selectable time of day, 5 minutes apart, as
 * `{ value: '13:05', label: '1:05 PM' }`.
 */
export const REPORT_TIME_OPTIONS = (() => {
  const options = [];
  for (
    let minutes = 0;
    minutes < 24 * 60;
    minutes += REPORT_TIME_STEP_MINUTES
  ) {
    const hour24 = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    options.push({
      value: `${String(hour24).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0',
      )}`,
      label: `${hour12}:${String(minute).padStart(2, '0')} ${
        hour24 < 12 ? 'AM' : 'PM'
      }`,
    });
  }
  return options;
})();

/**
 * Whatever the API returned -> a `{ weekday, time }` the pickers can hold.
 * A partial or unrecognised value falls back to the platform default field by
 * field, so a bad `time` doesn't also lose a good `weekday`.
 */
export const normalizeWeeklyReportSchedule = (schedule) => ({
  weekday:
    normalizeWeekday(schedule?.weekday) ||
    DEFAULT_WEEKLY_REPORT_SCHEDULE.weekday,
  time: isValidReportTime(schedule?.time)
    ? schedule.time
    : DEFAULT_WEEKLY_REPORT_SCHEDULE.time,
});

/** The `{ weekday, time }` payload the API stores. Both fields always sent. */
export const toWeeklyReportSchedulePayload = (schedule) =>
  normalizeWeeklyReportSchedule(schedule);

/** `null` when the pair is saveable, otherwise why it is not. */
export const validateWeeklyReportSchedule = (schedule) => {
  if (!normalizeWeekday(schedule?.weekday)) return 'Pick a day of the week.';
  if (!isValidReportTime(schedule?.time)) {
    return `Pick a time on a ${REPORT_TIME_STEP_MINUTES}-minute boundary.`;
  }
  return null;
};

/** `"13:05"` -> `"1:05 PM"`. */
export const formatReportTime = (time) =>
  REPORT_TIME_OPTIONS.find((option) => option.value === time)?.label ||
  time ||
  '';

/** `{ weekday: 'SUNDAY', time: '00:00' }` -> `"Sunday at 12:00 AM"`. */
export const formatWeeklyReportSchedule = (schedule) => {
  const normalized = normalizeWeeklyReportSchedule(schedule);
  const label = WEEKDAYS.find((day) => day.value === normalized.weekday)?.label;
  return `${label} at ${formatReportTime(normalized.time)}`;
};

/**
 * The human label for an IANA id, e.g.
 * `"America/Chicago"` -> `"(UTC-06:00) Central Time (US & Canada)"`.
 *
 * The picker only offers the first id of each entry, but an org's saved value
 * can be any zone the API accepted, so the whole `utc` list is searched before
 * falling back to showing the raw id.
 */
export const getTimeZoneLabel = (timeZone) => {
  if (!timeZone) return '';
  const entry = timeZones?.find((tz) => tz?.utc?.includes(timeZone));
  return entry?.text || timeZone;
};
